/**
 * LightningTabs, Salesforce Lightning style tab strip + panel.
 *
 * Used on record pages (admin + user connector detail, future connector-set
 * detail) to separate Overview / Tools / Related / Advanced into clear
 * focused panels.
 *
 * Pattern follows SF Lightning's tab pill anchored to the top of the page
 * body, panels swap inline without re-routing. The `value`/`onChange` API
 * lets the parent persist the active tab in URL hash or component state.
 *
 * Tab visibility supports `hidden` flag so we can role-gate the Advanced
 * tab (e.g. only admins/owners see it) per Ean's 2026-06-03 #7.
 *
 * PLAT-269 (PLAT-168 3c): built on Foundation's Tabs, TabList, Tab and
 * TabPanel (Radix), so arrow keys move between tabs and Tab then reaches the
 * panel. The public API is unchanged. Only the active tab's panel renders.
 *
 * PLAT-324 (PLAT-168 2b-ii): the overflow menu is Foundation's Menu, in the
 * pattern of ListRow's row menu (PLAT-249).
 */

import {
  Button,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from '@olytic/foundation';
import { type ReactNode, useRef } from 'react';

export interface LightningTab {
  /** Stable key. Used as the tab id and the data-tab marker. */
  id: string;
  /** Visible label. */
  label: string;
  /** Accepted for the call sites but not shown (PLAT-269): Foundation's Tab
   *  allows no record counts in tab labels. */
  count?: number;
  /** Render content for this tab. */
  render: () => ReactNode;
  /** When true, the tab is omitted entirely. */
  hidden?: boolean;
  /** Push #28 — optional leading icon (e.g. ObjectIcon for pillar tabs in
   *  Catalog). Renders flush-left of the label. Tabs that don't pass this
   *  fall back to label-only chrome unchanged. */
  icon?: ReactNode;
}

interface LightningTabsProps {
  tabs: LightningTab[];
  /** Currently active tab id. */
  value: string;
  /** Notify parent when user clicks a different tab. */
  onChange: (id: string) => void;
  /**
   * Push 4-1o (2026-07-09) — optional tab-overflow cap. When set to N,
   * the first N visible tabs render as tab buttons and any additional
   * tabs collapse into a "More ▾" dropdown at the end of the strip.
   *
   * The active tab is ALWAYS kept in the visible strip (it swaps with
   * the last visible slot if the active tab would otherwise overflow),
   * so the user never sees "the tab I picked disappeared into a menu."
   *
   * When unset, all tabs render side-by-side (previous behavior).
   */
  maxVisible?: number;
  /**
   * Push 4-1o (2026-07-09) — trigger label for the overflow dropdown.
   * Defaults to "More", which names the trigger "More actions"; any other
   * value names it as given.
   */
  moreLabel?: string;
}

export function LightningTabs({
  tabs,
  value,
  onChange,
  maxVisible,
  moreLabel = 'More',
}: LightningTabsProps) {
  const trigger = useRef<HTMLButtonElement | null>(null);
  const visible = tabs.filter((t) => t.hidden !== true);
  const active = visible.find((t) => t.id === value) ?? visible[0];

  // Push 4-1o overflow logic. When maxVisible is set and there are more
  // visible tabs than the cap, split into strip + overflow. The active
  // tab always lives in the strip so the user's current selection is
  // never buried under the More dropdown.
  const useOverflow = maxVisible !== undefined && visible.length > maxVisible;

  let stripTabs: LightningTab[] = visible;
  let overflowTabs: LightningTab[] = [];

  if (useOverflow && maxVisible !== undefined) {
    // First N tabs are the strip; the rest overflow.
    stripTabs = visible.slice(0, maxVisible);
    overflowTabs = visible.slice(maxVisible);

    // If the active tab is in overflow, swap it into the last strip slot
    // so the user's current position is always visible.
    const activeInOverflow = overflowTabs.some((t) => t.id === value);
    if (activeInOverflow) {
      const activeTab = overflowTabs.find((t) => t.id === value)!;
      const displaced = stripTabs[stripTabs.length - 1];
      if (displaced !== undefined) {
        stripTabs = [...stripTabs.slice(0, -1), activeTab];
        overflowTabs = overflowTabs.filter((t) => t.id !== value).concat(displaced);
      }
    }
  }

  // PLAT-269: `count` is accepted but not shown. Foundation's Tab house
  // rule: no record counts in tab labels (PM decision on the ticket).
  const triggerName = moreLabel === 'More' ? 'More actions' : moreLabel;

  // Every call site sits in a `.record-surface` card, so the strip and the
  // panel carry that card's insets themselves: 24px either side of the
  // strip, 28px by 32px around the panel. The strip row holds the TabList and
  // the overflow trigger, outside the tablist so it holds only tabs.
  return (
    <Tabs value={active?.id ?? ''} onValueChange={onChange}>
      <div className="flex items-end border-b border-[var(--border)] px-6">
        <TabList className="min-w-0 overflow-x-auto border-b-0 px-0">
          {stripTabs.map((t) => (
            <Tab key={t.id} value={t.id}>
              {t.icon !== undefined && <span aria-hidden="true">{t.icon}</span>}
              <span>{t.label}</span>
            </Tab>
          ))}
        </TabList>
        {overflowTabs.length > 0 && (
          <div className="ml-1 shrink-0 self-center">
            {/* Not modal, so an item can put focus back on the trigger before
               onChange runs (the menu-button pattern, PLAT-248/249). The
               trigger stays icon-only (Push 4-1t). */}
            <Menu modal={false}>
              <MenuTrigger asChild>
                <Button
                  ref={trigger}
                  variant="subtle"
                  size="icon"
                  aria-label={triggerName}
                  title={triggerName}
                >
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path
                      d="M4 6l4 4 4-4"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Button>
              </MenuTrigger>
              <MenuContent align="end">
                {overflowTabs.map((t) => (
                  <MenuItem
                    key={t.id}
                    onSelect={() => {
                      trigger.current?.focus();
                      onChange(t.id);
                    }}
                  >
                    {t.label}
                  </MenuItem>
                ))}
              </MenuContent>
            </Menu>
          </div>
        )}
      </div>
      {active !== undefined && (
        <TabPanel value={active.id} style={{ padding: '28px 32px' }}>
          {active.render()}
        </TabPanel>
      )}
    </Tabs>
  );
}
